import { useQuery } from '@tanstack/react-query';
import {
	getHoldingDetails,
	getHoldingEvents,
} from '@dealstack/frontend/app/services/Holdings.service.ts';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { CapTableRail, HoldingEventDto, HoldingEventRes } from '@dealstack/shared/lib/generated';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { formatAsOfFilterDateOnly } from '@dealstack/frontend/shared/utils/utc.ts';
import { parseBEDateIgnoreTimezone } from '@dealstack/shared/lib/format/dates/date.ts';

const useHoldingDetails = (
	personId: string | null,
	securityId: string | null,
	rail: CapTableRail,
	mipId?: string
) => {
	const request = useQuery({
		queryKey: ['holdingDetails', securityId, personId, mipId, rail],
		queryFn: () => {
			if (!securityId || !personId) {
				return Promise.resolve(null);
			}

			return getHoldingDetails({
				personId,
				securityId,
				mipId,
				rail,
			});
		},
		enabled: !!personId && !!securityId,
	});

	useEffect(() => {
		if (securityId && personId) {
			void request.refetch();
		}
	}, [securityId, personId, rail]);

	return request;
};

// CAP-1988: the log is rail-filtered — an event belongs to the rail whose interest it moved, so a
// beneficial owner on the legal rail sees none of the transfers that moved their beneficial
// interest. `rail` is in the query key, so toggling refetches.
const useHoldingEvents = (
	personId: string | null,
	securityId: string | null,
	rail: CapTableRail,
	filterDate?: string,
	mipId?: string
) => {
	const request = useQuery({
		queryKey: ['holdingEvents', securityId, personId, filterDate, mipId, rail],
		queryFn: () => {
			if (!securityId || !personId) {
				return Promise.resolve([]);
			}

			return getHoldingEvents({
				securityId,
				personId,
				filterDate,
				mipId,
				rail,
			});
		},
		enabled: !!personId && !!securityId,
	});

	useEffect(() => {
		if (securityId && personId) {
			void request.refetch();
		}
	}, [securityId, personId, filterDate, mipId, rail]);

	return request;
};

export const useHoldingDetailsController = () => {
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams(location.search);

	const personId = searchParams.get('holderId');
	const securityId = searchParams.get('securityId');
	const mipId = searchParams.get('mipId') ?? undefined;
	const filterDate = searchParams.get('filterDate') ?? undefined;
	// CAP-1062: the feature flag keeps every nominee surface dark. With the flag off the rail
	// toggle is hidden and the queries always request the beneficial (single-rail) projection,
	// even if a shared/bookmarked URL carries ?rail=LEGAL.
	const nomineeStructuresEnabled = useDealstackFeatureFlag(
		FEATURE_FLAG.CAP_NOMINEE_STRUCTURES
	).value;
	// Beneficial interest is the default rail; narrow the raw param without casting.
	const rail =
		nomineeStructuresEnabled && searchParams.get('rail') === CapTableRail.LEGAL
			? CapTableRail.LEGAL
			: CapTableRail.BENEFICIAL;

	// Rebuild the search params preserving the keys this page owns. filterDate/rail are only
	// serialised when meaningful so URLs stay clean (rail omitted while on the beneficial default).
	const updateParams = (overrides: { filterDate?: string | null; rail?: CapTableRail }) => {
		const nextFilterDate =
			overrides.filterDate !== undefined ? overrides.filterDate : filterDate ?? null;
		const nextRail = overrides.rail !== undefined ? overrides.rail : rail;

		const params: Record<string, string> = {
			holderId: personId ?? '',
			securityId: securityId ?? '',
			mipId: mipId ?? '',
		};
		if (nextFilterDate) {
			params.filterDate = nextFilterDate;
		}
		if (nextRail === CapTableRail.LEGAL) {
			params.rail = nextRail;
		}
		setSearchParams(params);
	};

	const setFilterDate = (date: Date | null) => {
		updateParams({
			filterDate: formatAsOfFilterDateOnly(date) ?? null,
		});
	};

	const setRail = (nextRail: CapTableRail) => {
		updateParams({ rail: nextRail });
	};

	const getHoldingDetailsRequest = useHoldingDetails(personId, securityId, rail, mipId);
	const getHoldingEventsRequest = useHoldingEvents(personId, securityId, rail, filterDate, mipId);
	// CAP-1988: whether the security sits in a nominee structure, and its name. Both describe the
	// security, so they are the same on both rails including one the holder holds nothing on.
	// CAP-1062: the feature flag keeps the rail toggle dark regardless of the security.
	const hasNomineeStructure =
		nomineeStructuresEnabled && (getHoldingDetailsRequest.data?.hasNomineeStructure ?? false);
	const securityName = getHoldingDetailsRequest.data?.securityName;

	const combinedEvents = useMemo(() => {
		const transactionEvents: HoldingEventDto[] = getHoldingEventsRequest.data || [];
		const holdingEvents: HoldingEventRes[] =
			getHoldingDetailsRequest.data?.security?.events || [];

		const combined: (HoldingEventDto | HoldingEventRes)[] = [
			...transactionEvents,
			...holdingEvents,
		];

		return combined.sort((a, b) => {
			const dateA = new Date(a.date);
			const dateB = new Date(b.date);
			return dateB.getTime() - dateA.getTime();
		});
	}, [getHoldingEventsRequest.data, getHoldingDetailsRequest.data?.security?.events]);

	return {
		getHoldingDetailsRequest,
		getHoldingEventsRequest,
		combinedEvents,
		filterDate: filterDate ? parseBEDateIgnoreTimezone(filterDate) : null,
		setFilterDate,
		rail,
		setRail,
		hasNomineeStructure,
		securityName,
	};
};
